import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { DeviceAuthFlow } from './DeviceAuthFlow';
import { DirectAuthFlow } from './DirectAuthFlow';

interface AuthHandlerProps {
  isInIframe: boolean;
  authMethod: 'direct' | 'device' | null;
}

export const AuthHandler: React.FC<AuthHandlerProps> = ({ authMethod }) => {
  const { accounts, inProgress } = useMsal();
  const navigate = useNavigate();

  // Check if user is already authenticated
  useEffect(() => {
    // Wait for MSAL to finish processing
    if (inProgress === 'none') {
      // Check if we have an authenticated account
      if (accounts.length > 0) {
        navigate('/dashboard');
      }
      
      // Also check localStorage for tokens (device flow, manual auth, or bypass)
      const authTokens = localStorage.getItem('auth_tokens');
      const bypassTokens = localStorage.getItem('bypass_auth_tokens');
      
      if (authTokens) {
        navigate('/dashboard');
      } else if (bypassTokens) {
        navigate('/dashboard');
      }
    }
  }, [accounts, inProgress, navigate]);
  // Show loading state while determining context
  if (authMethod === null) {
    return (
      <div className="context-detection">
        <div className="detection-content">
          <div className="spinner"></div>
          <h2>Detecting environment...</h2>
          <p>Determining the best authentication method for your context.</p>
        </div>
        
        <style>{`
          .context-detection {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            padding: 2rem;
          }

          .detection-content {
            text-align: center;
            background: white;
            padding: 3rem 2rem;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            max-width: 400px;
          }

          .detection-content h2 {
            color: #333;
            margin: 1rem 0 0.5rem 0;
          }

          .detection-content p {
            color: #666;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  // Render appropriate authentication flow based on context
  if (authMethod === 'device') {
    return (
      <div className="iframe-auth-container">
        <div className="context-info">
          <div className="context-badge">
            <span className="badge-icon">🖼️</span>
            <span>Iframe Mode</span>
          </div>
          <p>Using secure device flow authentication</p>
        </div>
        <DeviceAuthFlow />
      </div>
    );
  }

  if (authMethod === 'direct') {
    return (
      <div className="browser-auth-container">
        <div className="context-info">
          <div className="context-badge">
            <span className="badge-icon">🌐</span>
            <span>Browser Mode</span>
          </div>
          <p>Using direct Azure AD authentication</p>
        </div>
        <DirectAuthFlow />
      </div>
    );
  }

  // Fallback (should not reach here)
  return (
    <div className="auth-error">
      <h2>Authentication Error</h2>
      <p>Unable to determine authentication method.</p>
      <button onClick={() => window.location.reload()}>
        Reload Page
      </button>
    </div>
  );
};

